import { AnimatePresence, motion } from "framer-motion";
import {
  CheckIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LockIcon,
  PencilIcon,
  Trash2Icon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  SESSION_ROW_STAGGER_DELAY,
  sessionUrl,
  springGentle,
  springSnappy,
} from "../share-session-dialog-constants";
import { sharedSessionVersionDisplayText } from "../shared-session-version-selection";

import ReleaseLabel from "@/components/ReleaseLabel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { SharedSession } from "@/queries/prompts";

const DELETE_CONFIRMATION_TIMEOUT_MS = 4000;

export type SharedSessionCardUpdateFields = Partial<
  Pick<
    SharedSession,
    | "name"
    | "is_public"
    | "batch_enabled"
    | "chat_enabled"
    | "remix_enabled"
    | "label_name"
    | "version_number"
  >
>;

interface SharedSessionCardProps {
  animationIndex?: number;
  session: SharedSession;
  onUpdate: (fields: SharedSessionCardUpdateFields) => void;
  onDelete: () => void;
  leadingIcon: ReactNode;
  extraHeaderBadges?: ReactNode;
  expandedSettings: ReactNode;
}

export const SharedSessionCard = ({
  animationIndex = 0,
  session,
  onUpdate,
  onDelete,
  leadingIcon,
  extraHeaderBadges,
  expandedSettings,
}: SharedSessionCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(session.name);
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const versionText = useMemo(() => sharedSessionVersionDisplayText(session), [session]);
  const isDraftNameValid = draftName.trim().length > 0;

  useEffect(() => {
    if (!isEditingName) {
      setDraftName(session.name);
    }
  }, [isEditingName, session.name]);

  const commitNameChange = useCallback(() => {
    const trimmedName = draftName.trim();
    if (!trimmedName) {
      setDraftName(session.name);
      setIsEditingName(false);
      return;
    }

    if (trimmedName !== session.name) {
      onUpdate({ name: trimmedName });
    }

    setIsEditingName(false);
  }, [draftName, onUpdate, session.name]);

  const cancelNameEdit = useCallback(() => {
    setDraftName(session.name);
    setIsEditingName(false);
  }, [session.name]);

  const handleNameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commitNameChange();
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancelNameEdit();
      }
    },
    [cancelNameEdit, commitNameChange],
  );

  const handleInlineActionMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleTrashClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setConfirmingDelete(true);
    cancelTimeoutRef.current = setTimeout(() => {
      setConfirmingDelete(false);
    }, DELETE_CONFIRMATION_TIMEOUT_MS);
  }, []);

  const handleCancelDelete = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setConfirmingDelete(false);
    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
    }
  }, []);

  const handleConfirmDelete = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
      }
      onDelete();
    },
    [onDelete],
  );

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{
        ...springGentle,
        delay: animationIndex * SESSION_ROW_STAGGER_DELAY,
        layout: { duration: 0.2 },
      }}
      className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/30"
    >
      <button
        type="button"
        onClick={() => !confirmingDelete && !isEditingName && setExpanded(prev => !prev)}
        className="group flex w-full min-w-0 items-center gap-1 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          {leadingIcon}
          <div className="flex min-w-0 flex-col">
            <div className="flex min-w-0 items-center gap-1">
              {isEditingName ? (
                <div
                  className="flex min-w-0 items-center gap-0.5"
                  onClick={event => event.stopPropagation()}
                >
                  <Input
                    value={draftName}
                    onChange={event => setDraftName(event.target.value)}
                    onBlur={commitNameChange}
                    onKeyDown={handleNameKeyDown}
                    maxLength={512}
                    className="h-6 min-w-0 rounded-md border border-transparent bg-transparent px-1.5 text-sm font-medium leading-tight text-gray-800 shadow-none focus-visible:border-gray-200 focus-visible:bg-white focus-visible:ring-0 dark:text-gray-200 dark:focus-visible:border-gray-700 dark:focus-visible:bg-gray-900"
                    aria-label="Share link name"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onMouseDown={handleInlineActionMouseDown}
                    onClick={event => {
                      event.stopPropagation();
                      cancelNameEdit();
                    }}
                    className="h-5 w-5 shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    aria-label="Cancel rename"
                  >
                    <XIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onMouseDown={handleInlineActionMouseDown}
                    onClick={event => {
                      event.stopPropagation();
                      if (isDraftNameValid) {
                        commitNameChange();
                      }
                    }}
                    disabled={!isDraftNameValid}
                    className="h-5 w-5 shrink-0 text-gray-400 hover:text-emerald-600 disabled:opacity-40 dark:text-gray-500 dark:hover:text-emerald-400"
                    aria-label="Save rename"
                  >
                    <CheckIcon className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <p
                    className="truncate text-sm font-medium text-gray-800 dark:text-gray-200"
                    title={session.name}
                  >
                    {session.name}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={event => {
                      event.stopPropagation();
                      setIsEditingName(true);
                    }}
                    className="h-5 w-5 shrink-0 text-gray-300 opacity-0 transition-opacity hover:text-gray-500 focus-visible:opacity-100 group-hover:opacity-100 dark:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Edit link name"
                  >
                    <PencilIcon className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
            {session.label_name ? (
              <ReleaseLabel
                label={{ id: 0, name: session.label_name }}
                size="mini"
                readOnly
                showDeleteButton={false}
              />
            ) : (
              versionText && (
                <p className="truncate text-[11px] leading-tight text-muted-foreground/50">
                  {versionText}
                </p>
              )
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            {session.is_public ? (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                <GlobeIcon className="h-2.5 w-2.5" /> Public
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                <LockIcon className="h-2.5 w-2.5" /> Private
              </span>
            )}
            {extraHeaderBadges}
          </div>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={springSnappy}
          className="shrink-0 text-gray-400"
        >
          <ChevronDownIcon className="h-4 w-4" />
        </motion.div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={event => {
            event.stopPropagation();
            window.open(sessionUrl(session.share_token), "_blank");
          }}
          className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-600"
          aria-label="Open in new tab"
        >
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleTrashClick}
          className={cn(
            "h-7 w-7 shrink-0 transition-colors",
            confirmingDelete
              ? "text-red-400 hover:text-red-500"
              : "text-gray-300 hover:text-red-400 dark:text-gray-600 dark:hover:text-red-400",
          )}
          aria-label="Delete link"
        >
          <Trash2Icon className="h-3.5 w-3.5" />
        </Button>
      </button>

      <AnimatePresence initial={false}>
        {confirmingDelete && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="flex items-center justify-between border-t border-red-100 bg-red-50/60 px-3 py-2 dark:border-red-900/40 dark:bg-red-950/20">
              <div className="flex items-center gap-1.5">
                <TriangleAlertIcon className="h-3 w-3 shrink-0 text-red-400" />
                <span className="text-[12px] text-red-600/80 dark:text-red-400/80">
                  Delete this link permanently?
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelDelete}
                  className="h-6 px-2 text-[11px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleConfirmDelete}
                  className="h-6 gap-1 rounded-md bg-red-500 px-2.5 text-[11px] font-medium text-white shadow-none hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-500"
                >
                  <Trash2Icon className="h-3 w-3" />
                  Delete
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="min-w-0 border-t border-gray-100 px-3 pb-3 pt-2 dark:border-gray-700">
              {expandedSettings}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
